import React, { useState, useEffect } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { Typography, Box } from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist';
import axios from 'axios';
import Swal from 'sweetalert2';

const PdfPreview = ({ pdfUrl }) => {
  const [workerError, setWorkerError] = useState(null);
  const [isViewerReady, setIsViewerReady] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [], // Remove sidebar tabs
    renderToolbar: (Toolbar) => (
      <Toolbar>
        {(slots) => {
          const {
            Download,
            Print,
            Zoom,
            ZoomIn,
            ZoomOut,
          } = slots;
          return (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                width: '100%',
                padding: '4px 8px',
              }}
            >
              <div style={{ padding: '0px 2px' }}>
                <ZoomOut />
              </div>
              <div style={{ padding: '0px 2px' }}>
                <Zoom />
              </div>
              <div style={{ padding: '0px 2px' }}>
                <ZoomIn />
              </div>
              <div style={{ padding: '0px 2px', marginLeft: 'auto' }}>
                <Download />
              </div>
              <div style={{ padding: '0px 2px' }}>
                <Print />
              </div>
            </div>
          );
        }}
      </Toolbar>
    ),
  });

  // Configure PDF worker
  const workerSrc = '/pdf.worker.min.js';
  const fallbackWorkerSrc = 'https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js';
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

  // Test worker availability
  useEffect(() => {
    const testWorker = async () => {
      try {
        let response = await fetch(workerSrc);
        if (!response.ok) {
          console.warn(`Local worker failed: ${response.statusText}. Trying CDN fallback.`);
          response = await fetch(fallbackWorkerSrc);
          if (!response.ok) {
            throw new Error(`CDN worker failed: ${response.statusText}`);
          }
          pdfjsLib.GlobalWorkerOptions.workerSrc = fallbackWorkerSrc;
        }
        setWorkerError(null);
        setIsViewerReady(true);
      } catch (err) {
        setWorkerError('Failed to load PDF worker. PDF previews may be unavailable.');
        pdfjsLib.GlobalWorkerOptions.workerSrc = '';
        setIsViewerReady(false);
        Swal.fire({
          icon: 'warning',
          title: 'PDF Worker Error',
          text: 'PDF previews may be unavailable due to worker loading failure.',
          timer: 1500,
        });
      }
    };
    testWorker();
  }, []);

  // Fetch PDF with authentication
  useEffect(() => {
    const fetchPdf = async () => {
      if (!pdfUrl) {
        setPdfDataUrl(null);
        setFetchError(null);
        return;
      }
      try {
        console.log('Fetching PDF from:', pdfUrl);
        const token = localStorage.getItem('authToken');
        if (!token) throw new Error('No authentication token found.');
        const response = await axios.get(pdfUrl, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'arraybuffer',
        });
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(pdfBlob);
        setPdfDataUrl(blobUrl);
        setFetchError(null);
      } catch (err) {
        console.error('Error fetching PDF:', err);
        const errorMsg = err.response?.status === 403
          ? 'Authentication failed. Please log in again.'
          : `Failed to load PDF: ${err.response?.data?.message || err.message}`;
        setFetchError(errorMsg);
        setPdfDataUrl(null);
        Swal.fire({
          icon: 'error',
          title: 'PDF Load Failed',
          text: errorMsg,
          timer: 1500,
        });
      }
    };
    fetchPdf();
    return () => {
      if (pdfDataUrl) {
        URL.revokeObjectURL(pdfDataUrl);
      }
    };
  }, [pdfUrl]);

  // Error boundary for Viewer
  useEffect(() => {
    const handleError = (event) => {
      if (event.error && event.error.name === 'TypeError' && event.error.message.includes('current')) {
        console.error('Caught ref error in Viewer:', event.error);
        setFetchError('PDF rendering failed due to an internal error.');
        setIsViewerReady(false);
      }
    };
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  return (
    <Box 
      className="pdf-preview" 
      sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '649px', 
        overflow: 'hidden' 
      }}
    >
      <Box className="card pdf-container">
        {pdfUrl && !workerError && !fetchError && isViewerReady && pdfDataUrl ? (
          <Worker workerUrl={pdfjsLib.GlobalWorkerOptions.workerSrc}>
            <Viewer
              fileUrl={pdfDataUrl}
              plugins={[defaultLayoutPluginInstance]}
              onDocumentLoad={() => console.log('PDF loaded successfully')}
              renderError={(error) => (
                <Typography className="error-text">
                  Failed to render PDF: {error.message}
                </Typography>
              )}
            />
          </Worker>
        ) : (
          <Typography className={workerError || fetchError ? 'error-text' : 'placeholder-text'}>
            {workerError || fetchError || 'No PDF selected'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PdfPreview;